import { useState, useEffect, useMemo } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { Refresh, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useSnackbar, useRolePermissions, useScrollSync } from '@/hooks';
import {
  useDownloadOrderMutation,
  useGetOrdersFiltersQuery,
  useGetOrdersQuery,
  useGetNumberHistoryQuery
} from '@/services/orders.api';

import { useAppSelector } from '../../store';

import { Order, OrdersFilters } from '@/types/orders.types';
import {
  FiltersSection,
  OrdersTable,
  HistoryTable,
  OrdersTabs,
  OrderNumberDialog,
  TableHeaders,
  getNextFilters,
  getFiltersAfterRemove,
  getActiveFilters,
  defaultFilters
} from './components';

import './OrdersPage.css';

function OrdersPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canGenerateOrders } = useRolePermissions();
  // TODO: do we need it at all?
  const hasSearched = true;

  const { setSnackbarMessage } = useSnackbar();
  const { headerRef: ordersHeaderRef, bodyRef: ordersBodyRef } = useScrollSync();
  const { headerRef: historyHeaderRef, bodyRef: historyBodyRef } = useScrollSync();

  const isAuthorized = useAppSelector(state => state.global.isAuthorized);

  const [filters, setFilters] = useState<OrdersFilters>(defaultFilters);
  const [selectedTab, setSelectedTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const isCollapsible = isMobile;

  const {
    data: filterOptions = { divisions: [], legal_entities: [] },
    isLoading: filterOptionsLoading,
    isError: filterOptionsError
  } = useGetOrdersFiltersQuery(filters.division_id, {
    skip: !isAuthorized,
    refetchOnMountOrArgChange: true
  });

  const { data: { orders = [] } = {}, isError: ordersError } = useGetOrdersQuery(filters, {
    skip: !isAuthorized || selectedTab !== 0,
    refetchOnMountOrArgChange: true
  });

  const {
    data: numberingHistory = [],
    isError: numberHistoryError,
    refetch: refetchNumberHistory
  } = useGetNumberHistoryQuery(filters, {
    skip: !isAuthorized || selectedTab !== 1,
    refetchOnMountOrArgChange: true
  });
  const [downloadOrder] = useDownloadOrderMutation();

  useEffect(() => {
    if (filterOptionsError) {
      setSnackbarMessage('Помилка завантаження параметрів фільтрів', 'error');
    }
  }, [filterOptionsError, setSnackbarMessage]);

  useEffect(() => {
    if (filterOptionsError) {
      setSnackbarMessage('Помилка завантаження параметрів фільтрів', 'error');
    }
  }, [filterOptionsError, setSnackbarMessage]);

  useEffect(() => {
    if (ordersError) {
      setSnackbarMessage('Помилка завантаження розпоряджень', 'error');
    }
  }, [ordersError, setSnackbarMessage]);

  useEffect(() => {
    if (numberHistoryError) {
      setSnackbarMessage('Помилка завантаження історії змін', 'error');
    }
  }, [numberHistoryError, setSnackbarMessage]);

  useEffect(() => {
    if (!Array.isArray(numberingHistory)) {
      setSnackbarMessage('Помилка формату даних історії змін', 'error');
    }
  }, [numberingHistory, setSnackbarMessage]);

  const activeFilters = useMemo(() => getActiveFilters(filters, filterOptions), [filters, filterOptions]);

  const handleChangeFilters = (prop: keyof OrdersFilters, value?: string | number | number[]) => {
    const nextFilters = getNextFilters(filters, prop, value);
    setFilters(nextFilters);
  };

  const handleRemoveFilter = (key: string, id?: number) => {
    const nextFilters = getFiltersAfterRemove(filters, key, id);
    setFilters(nextFilters);
  };

  const handleTabChange = (newValue: number) => {
    if (newValue === 1 && !canGenerateOrders) return;
    setSelectedTab(newValue);
  };

  const handleDownload = async (order: Order) => {
    try {
      const filename = await downloadOrder(order.id).unwrap();
      setSnackbarMessage(`Файл ${filename} завантажено`, 'success');
    } catch (error: any) {
      const raw = error?.error || '';
      const message = (typeof raw === 'string' ? raw.replace(/^Error:\s*/, '') : null)
        || error?.data?.error
        || 'Помилка завантаження файлу';
      setSnackbarMessage(message, 'error');
    }
  };

  const handleHistoryRefetch = () => {
    if (selectedTab === 1) refetchNumberHistory();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (selectedTab === 1) refetchNumberHistory();
  };

  return (
    <div className={`orders-container ${isMobile ? 'mobile' : ''}`}>
      {filterOptionsLoading ? (
        <div className="orders-loading-container">
          <Refresh className="orders-loading-spinner" sx={{ fontSize: 40 }} />
          <p>Завантаження даних...</p>
        </div>
      ) : (
        <>
          {/* Sticky Header */}
          <div className={`orders-sticky-header ${isMobile ? 'mobile' : ''}`}>
            <div className={`orders-collapsible${isCollapsible ? (isExpanded ? ' expanded' : ' collapsed') : ''}`}>
              <FiltersSection
                filters={filters}
                activeFilters={activeFilters}
                filterOptions={filterOptions}
                canGenerateOrders={canGenerateOrders}
                onFilterChange={handleChangeFilters}
                onRemoveFilter={handleRemoveFilter}
                onChangeOrderNumber={() => setDialogOpen(true)}
              />
            </div>

            {isCollapsible && (
              <button className="orders-toggle-bar" onClick={() => setIsExpanded(v => !v)}>
                <span className="orders-toggle-summary">
                  {!isExpanded && filters.division_id
                    ? filterOptions.divisions.find(d => d.id === filters.division_id)?.name || 'Фільтри'
                    : isExpanded
                      ? 'Згорнути фільтри'
                      : 'Розгорнути фільтри'}
                </span>
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </button>
            )}

            <OrdersTabs selectedTab={selectedTab} onChange={handleTabChange} canGenerateOrders={canGenerateOrders} />

            <TableHeaders
              selectedTab={selectedTab}
              ordersHeaderRef={ordersHeaderRef}
              historyHeaderRef={historyHeaderRef}
            />
          </div>

          {/* Table bodies */}
          {selectedTab === 0 && (
            <OrdersTable
              ordersData={orders}
              hasSearched={hasSearched}
              onDownload={handleDownload}
              bodyRef={ordersBodyRef}
            />
          )}
          {selectedTab === 1 && <HistoryTable numberingHistory={numberingHistory} bodyRef={historyBodyRef} />}
        </>
      )}

      <OrderNumberDialog open={dialogOpen} onClose={handleDialogClose} onHistoryRefetch={handleHistoryRefetch} />
    </div>
  );
}

export default OrdersPage;
